import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, VideoCamera, MapPin, Sparkle, Eye, HandHeart, Leaf,
  BookOpen, FacebookLogo, InstagramLogo, Calendar, Pulse,
} from "@phosphor-icons/react";

const FACEBOOK_URL = "https://www.facebook.com/intuitiveangelagehl";
const INSTAGRAM_URL = "https://www.instagram.com/its.angelagehl";
const BOOKING_URL = "https://thesmilinggoddess.fullslate.com";

// EDIT THESE — paste real client testimonials here when you have them.
// Keep them short (1–3 sentences). Use first name + city for trust without breaking privacy.
const TESTIMONIALS = [
  {
    quote: "I have been working with Angela for 3 months and have made more progress with working through things that hold me back than I did seeing a counsellor for years. If you're stuck… see Angela! She is amazing.",
    name: "Sarah J",
    location: "Verified client",
    image: "https://customer-assets.emergentagent.com/job_reflect-grow-15/artifacts/8ho488if_IMG_9863.jpeg",
  },
  {
    quote: "Working with Angela has changed my life. She helped me get myself on track.",
    name: "Jenn B",
    location: "Regina, SK",
    image: "https://customer-assets.emergentagent.com/job_reflect-grow-15/artifacts/t22hdk4v_IMG_9860.jpeg",
  },
];

const ONE_ON_ONE_DESCRIPTION = "1:1 sessions blend intuitive guidance with evidence-based coaching to give clients the skills, clarity, and focus to live their best lives.";

const FORMATS = [
  { icon: MapPin, label: "In person", text: "Saskatoon, SK" },
  { icon: Phone, label: "By phone", text: "Anywhere in North America" },
  { icon: VideoCamera, label: "Via Zoom", text: "Worldwide" },
];

const JOURNALS = [
  {
    id: "journey90",
    icon: BookOpen,
    title: "Rediscover Yourself",
    sub: "90-day self-discovery",
    price: "$29.99",
    note: "7-day free trial",
    color: "moss",
    desc: "A gentle 90-day journey of daily AI-curated prompts, mood tracking, goals, gratitude and habits. For anyone meeting themselves again, or for the first time.",
  },
  {
    id: "grief180",
    icon: HandHeart,
    title: "Moving through Grief",
    sub: "6-month grief & loss companion",
    price: "$39.99",
    note: "Lifetime · no trial",
    color: "clay",
    desc: "180 days of tender prompts, a place to capture memories of loved ones, traditional spirit signs to watch for, and uplifting quotes for the hardest mornings.",
  },
  {
    id: "sober30",
    icon: Leaf,
    title: "New Beginnings",
    sub: "30-day sober living journal",
    price: "$15.99",
    note: "Lifetime · no trial",
    color: "sage",
    desc: "30 days of honest prompts, trigger tracking by category and intensity, milestone celebrations from Day 1 to Day 30, and quiet inspiration when it's needed most.",
  },
  {
    id: "chronic365",
    icon: Pulse,
    title: "Living Well",
    sub: "365-day chronic health companion",
    price: "$49.99",
    note: "7-day free trial",
    color: "clay",
    desc: "A full year of daily prompts, symptom tracking, sleep logging, nutrition tips, healthy-living reminders, and 12 progress milestones. For life with any chronic condition.",
  },
];

export default function Services() {
  return (
    <div className="bg-sand text-charcoal">
      {/* NAV */}
      <header className="sticky top-0 z-30 glass border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-moss"></span>
            <span className="font-serif text-xl md:text-2xl tracking-tight leading-tight">
              Rediscover Yourself <span className="text-clay text-sm md:text-base block md:inline">· with Angela Gehl</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-2">
            <Link to="/about" className="text-sm px-4 py-2 rounded-full hover:bg-cream">About Angela</Link>
            <Link to="/login" className="text-sm px-4 py-2 rounded-full hover:bg-cream">Sign in</Link>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
               className="text-sm px-5 py-2 rounded-full bg-moss text-white hover:bg-mossDark">
              Book a session
            </a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-5">Services with Angela</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.05] font-medium mb-6">
            Two ways to walk with Angela —<br/>
            <em className="text-clay not-italic">in person</em> or on the page.
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Choose a private 1:1 session, or one of the four guided interactive journals.
            Every offering is built on Angela's 15+ years of intuitive practice, Harvard-trained coaching,
            and certifications across multiple healing modalities.
          </p>
        </div>
      </section>

      {/* THE 3 JOURNALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" id="journals">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">Interactive Journals</p>
          <h2 className="font-serif text-3xl md:text-5xl font-medium leading-tight">
            Four guided journeys, available on the website.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {JOURNALS.map((j) => (
            <div key={j.title} data-testid={`journal-${j.title.toLowerCase().replace(/\s+/g, '-')}`}
                 className="bg-white rounded-3xl border border-border p-8 soft-shadow hover:soft-shadow-lg hover:-translate-y-1 transition-all flex flex-col">
              <div className={`w-12 h-12 rounded-2xl bg-cream flex items-center justify-center mb-5`}>
                <j.icon size={24} weight="duotone" className={
                  j.color === "moss" ? "text-moss" : j.color === "clay" ? "text-clay" : "text-sage"
                } />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">{j.sub}</p>
              <h3 className="font-serif text-2xl mb-3 font-medium">{j.title}</h3>
              <p className="text-muted-foreground leading-relaxed mb-5 flex-1">{j.desc}</p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-serif text-3xl font-medium">{j.price}</span>
                <span className="text-sm text-muted-foreground">one-time</span>
              </div>
              <p className="text-xs text-muted-foreground mb-5">{j.note}</p>
              <Link to={`/register?journal=${j.id}`}
                    data-testid={`journal-cta-${j.id}`}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-white text-sm transition-all ${
                      j.color === "moss" ? "bg-moss hover:bg-mossDark"
                      : j.color === "clay" ? "bg-clay hover:bg-clay/90"
                      : "bg-sage hover:bg-sage/90"
                    }`}>
                Begin <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>

        {/* Bundle banner */}
        <div className="mt-8 bg-cream/70 border border-border rounded-3xl p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">Best value</p>
            <h3 className="font-serif text-2xl">All four journals · $109.99 lifetime</h3>
            <p className="text-muted-foreground text-sm">Save $25.97 when you take all four at once.</p>
          </div>
          <Link to="/register?journal=bundle" data-testid="services-bundle-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-charcoal text-white hover:bg-charcoal/90 transition-all">
            Get the bundle <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CREDENTIALS / CERTIFICATES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 border-t border-border/40" id="credentials">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">Credentials</p>
          <h2 className="font-serif text-3xl md:text-5xl font-medium leading-tight mb-6">
            Trained in science. Practised in spirit.
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Angela's work bridges clinical, evidence-informed coaching with traditional energy
            practice — formally certified in both worlds.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-3xl border border-border p-6 soft-shadow">
            <div className="rounded-2xl overflow-hidden border-2 border-border bg-white p-1 mb-5">
              <img
                src="https://customer-assets.emergentagent.com/job_reflect-grow-15/artifacts/5rlqodgc_IMG_9859.jpeg"
                alt="Angela Gehl — 2026 Canadian Choice Award winner, Life Coaches category, Saskatoon"
                data-testid="canadian-choice-award"
                className="w-full h-auto rounded-lg"
                loading="lazy"
              />
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">Canadian Choice Award · 2026</p>
            <h3 className="font-serif text-2xl mb-2">Life Coach of the Year</h3>
            <p className="text-sm text-muted-foreground">
              Winner of the 2026 Canadian Choice Award in the Life Coaches category for
              Saskatoon — awarded to Angela Gehl, AMG Consulting.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-border p-6 soft-shadow">
            <div className="rounded-2xl overflow-hidden border-2 border-border bg-white p-1 mb-5">
              <img
                src="https://customer-assets.emergentagent.com/job_reflect-grow-15/artifacts/160lmev8_IMG_0876.jpeg"
                alt="Angela Gehl — Harvard Medical School Executive Education certificate in Lifestyle and Wellness Coaching, June 2025"
                data-testid="harvard-certificate"
                className="w-full h-auto rounded-lg"
                loading="lazy"
              />
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">Harvard Medical School · Executive Education</p>
            <h3 className="font-serif text-2xl mb-2">Lifestyle and Wellness Coaching</h3>
            <p className="text-sm text-muted-foreground">
              Completed June 2025. A clinical, evidence-informed framework for sustainable
              behaviour change — issued under Associate Professor Beth Frates, MD.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-border p-6 soft-shadow">
            <div className="rounded-2xl overflow-hidden border-2 border-border bg-white p-1 mb-5">
              <img
                src="/reiki-certificate.jpg"
                alt="Angela Gehl — Reiki Master Practitioner certification"
                data-testid="reiki-certificate"
                className="w-full h-auto rounded-lg"
                loading="lazy"
              />
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">Usui/Tibetan Reiki</p>
            <h3 className="font-serif text-2xl mb-2">Reiki Master Practitioner</h3>
            <p className="text-sm text-muted-foreground">
              Master Practitioner level of the Usui/Tibetan Reiki System of Natural Health
              (May 2023, under Reiki Master/Teacher Vanessa Lamont) — one of several healing
              modalities Angela has formally trained in.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-border p-6 soft-shadow flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-cream flex items-center justify-center mb-5">
                <span className="font-serif text-2xl text-moss">Ph.D</span>
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">Doctorate</p>
              <h3 className="font-serif text-2xl mb-2">Neuropsychology</h3>
              <p className="text-sm text-muted-foreground">
                Doctoral-level study and credential in neuropsychology — the science of how the
                brain shapes behaviour, emotion, and recovery. Informs every framework Angela uses.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-border p-6 soft-shadow flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-cream flex items-center justify-center mb-5">
                <Sparkle size={28} weight="duotone" className="text-clay" />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">Certified Practitioner</p>
              <h3 className="font-serif text-2xl mb-2">Past Life Regression</h3>
              <p className="text-sm text-muted-foreground">
                Certified in Past Life Regression therapy — a contemplative practice for surfacing
                the patterns, fears, and gifts that travel with us across lifetimes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 border-t border-border/40" id="testimonials">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">In their words</p>
          <h2 className="font-serif text-3xl md:text-5xl font-medium leading-tight">
            What clients say.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} data-testid={`testimonial-${i}`}
                    className="bg-white rounded-3xl border border-border p-7 soft-shadow flex flex-col">
              <div className="flex items-center gap-1 mb-3 text-clay text-sm">
                {"★★★★★".split("").map((s, idx) => <span key={idx}>{s}</span>)}
              </div>
              <blockquote className="font-serif text-lg leading-relaxed text-charcoal flex-1 mb-5">
                "{t.quote}"
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-border">
                {t.image ? (
                  <img src={t.image} alt={t.name}
                       className="w-10 h-10 rounded-full object-cover border border-border" loading="lazy"/>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-cream flex items-center justify-center font-serif text-moss">
                    {t.name.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="font-medium text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.location}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ONE-ON-ONE SESSIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" id="one-on-one">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">1:1 Private Sessions</p>
            <h2 className="font-serif text-3xl md:text-5xl font-medium">Sit with Angela, directly.</h2>
          </div>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
             data-testid="services-book-now"
             className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark transition-all">
            Book a session <ArrowRight size={16} />
          </a>
        </div>

        {/* Formats strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {FORMATS.map((f) => (
            <div key={f.label} data-testid={`format-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
                 className="bg-white rounded-2xl border border-border p-5 flex items-center gap-4 soft-shadow">
              <div className="w-11 h-11 rounded-xl bg-cream flex items-center justify-center">
                <f.icon size={20} weight="duotone" className="text-moss" />
              </div>
              <div>
                <p className="font-serif text-lg leading-tight">{f.label}</p>
                <p className="text-xs text-muted-foreground">{f.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Session description */}
        <div className="relative bg-white rounded-3xl border border-border p-8 md:p-12 soft-shadow-lg overflow-hidden">
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-sage/15 blur-3xl"></div>
          <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-clay/10 blur-3xl"></div>
          <div className="relative max-w-3xl">
            <div className="w-12 h-12 rounded-2xl bg-cream flex items-center justify-center mb-6">
              <Sparkle size={24} weight="duotone" className="text-moss" />
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-charcoal mb-6" data-testid="one-on-one-description">
              {ONE_ON_ONE_DESCRIPTION}
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
               data-testid="session-description-cta"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark transition-all">
              Book your session <ArrowRight size={16} />
            </a>
          </div>
        </div>

        <div className="mt-10 bg-charcoal text-white rounded-3xl p-8 md:p-10 soft-shadow-lg relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-clay/30 blur-3xl"></div>
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-clay font-bold mb-2 flex items-center gap-2">
                <Calendar size={14} weight="duotone"/> Real-time availability
              </p>
              <h3 className="font-serif text-2xl md:text-3xl mb-2">See Angela's open times</h3>
              <p className="text-white/70">Booking is handled securely through Full Slate. Pick your format and time in under a minute.</p>
            </div>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
               data-testid="services-fullslate-cta"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay hover:bg-clay/90 transition-all">
              Open booking page <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL DEVELOPMENT WORKSHOPS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 border-t border-border/40" id="workshops">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_440px] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">For Organizations</p>
            <h2 className="font-serif text-3xl md:text-5xl font-medium leading-tight mb-6">
              Professional Development Workshops.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Angela leads in-person and virtual workshops for teams, businesses, and
              organizations looking to build healthier cultures and stronger humans.
            </p>
            <ul className="space-y-2.5 mb-8">
              {[
                "Team building",
                "Professional communication",
                "Conflict resolution",
                "Ethics & overcoming bias in the workplace",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3" data-testid={`workshop-topic-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-clay mt-2.5 shrink-0"></span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
               data-testid="workshops-cta"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark transition-all">
              Inquire about a workshop <ArrowRight size={16} />
            </a>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-clay/15 blur-3xl"></div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-sage/20 blur-3xl"></div>
            <img
              src="https://customer-assets.emergentagent.com/job_reflect-grow-15/artifacts/ijpot0tr_IMG_9861.jpeg"
              alt="Professional Development Workshops by Angela Gehl"
              data-testid="workshops-graphic"
              className="relative w-full h-auto rounded-3xl border border-border soft-shadow-lg"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/60 mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-moss"></span>
            <span className="font-serif text-lg">Rediscover Yourself · with Angela Gehl</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FacebookLogo size={18} weight="duotone"/></a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram"><InstagramLogo size={18} weight="duotone"/></a>
            <a href="https://www.angelagehl.ca" className="hover:text-charcoal">www.angelagehl.ca</a>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="hover:text-charcoal">Book a session</a>
            <span>© {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
